import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, ChevronDown, LogOut, ExternalLink, CreditCard } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useLocalePath } from '../../hooks/useLocalePath'
import { SUPPORTED_LOCALES, type SupportedLocale } from '../../i18n'
import { setCachedLocale } from '../../utils/localeCache'
import styles from './Navbar.module.css'

const LOCALE_META: Record<string, { native: string }> = {
  en: { native: 'English' },
  fr: { native: 'Fran\u00E7ais' },
  de: { native: 'Deutsch' },
}

const LOCALE_FLAG: Record<string, string> = {
  en: 'gb', fr: 'fr', de: 'de',
}

export default function Navbar() {
  const { t } = useTranslation('common')
  const lp = useLocalePath()
  const location = useLocation()
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)
  const { user, loading, signOut } = useAuth()

  const appUrl = import.meta.env.VITE_APP_URL || 'https://app.carbon13.com'

  const pathLang = location.pathname.split('/').filter(Boolean)[0]
  const currentLocale: SupportedLocale = SUPPORTED_LOCALES.includes(pathLang as never)
    ? (pathLang as SupportedLocale)
    : 'en'

  const navLinks = [
    { path: '/services', labelKey: 'nav.services' },
    { path: '/projects', labelKey: 'nav.projects' },
    { path: '/about', labelKey: 'nav.about' },
    { path: '/pricing', labelKey: 'nav.pricing' },
    { path: '/contact', labelKey: 'nav.contact' },
  ]

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsUserMenuOpen(false)
  }, [location])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setIsUserMenuOpen(false)
  }

  const handleLocaleSwitch = (newLocale: SupportedLocale) => {
    setCachedLocale(newLocale)
    const pathWithoutLocale = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/')
    navigate(`/${newLocale}${pathWithoutLocale === '/' ? '' : pathWithoutLocale}${location.search}${location.hash}`)
    setIsMobileMenuOpen(false)
  }

  const getInitials = (name: string | null, email: string | null): string => {
    if (name) return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    if (email) return email[0].toUpperCase()
    return 'U'
  }

  return (
    <nav className={`${styles.navbar} ${isScrolled ? styles.scrolled : ''}`}>
      <div className={styles.container}>
        <Link to={lp('/')} className={styles.logo}>
          <svg className={styles.logoMark} viewBox="0 0 44 44" width="34" height="34" fill="none">
            <circle cx="31.8" cy="8.1" r="2" fill="#0D9488"/>
            <circle cx="27.6" cy="6.0" r="2" fill="#1A9280"/>
            <circle cx="23.1" cy="5.0" r="2" fill="#279078"/>
            <circle cx="18.5" cy="5.4" r="2" fill="#338F70"/>
            <circle cx="14.2" cy="6.9" r="2" fill="#408D68"/>
            <circle cx="10.4" cy="9.6" r="2" fill="#4D8B5F"/>
            <circle cx="7.5" cy="13.2" r="2" fill="#5A8957"/>
            <circle cx="5.6" cy="17.4" r="2" fill="#66874F"/>
            <circle cx="5.0" cy="22.0" r="2" fill="#738547"/>
            <circle cx="5.6" cy="26.6" r="2" fill="#80843F"/>
            <circle cx="7.5" cy="30.8" r="2" fill="#8D8237"/>
            <circle cx="10.4" cy="34.4" r="2" fill="#99802F"/>
            <circle cx="14.2" cy="37.1" r="2" fill="#A67E27"/>
            <circle cx="18.5" cy="38.6" r="2" fill="#B37C1E"/>
            <circle cx="23.1" cy="39.0" r="2" fill="#C07B16"/>
            <circle cx="27.6" cy="38.0" r="2" fill="#CC790E"/>
            <circle cx="31.8" cy="35.9" r="2" fill="#D97706"/>
          </svg>
          <span className={styles.logoText}>CARBON<span className={styles.logoAccent}>13</span></span>
        </Link>

        <div className={styles.desktopNav}>
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={lp(link.path)}
              className={`${styles.navLink} ${location.pathname.includes(link.path) ? styles.active : ''}`}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {loading ? (
            <div className={styles.authLoading}></div>
          ) : user ? (
            <div className={styles.userMenu} ref={userMenuRef}>
              <button className={styles.userButton} onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}>
                <span className={styles.userAvatar}>{getInitials(user.displayName, user.email)}</span>
                <ChevronDown size={16} className={isUserMenuOpen ? styles.chevronOpen : ''} />
              </button>
              {isUserMenuOpen && (
                <div className={styles.userDropdown}>
                  <div className={styles.userInfo}>
                    <span className={styles.userName}>{user.displayName || 'User'}</span>
                    <span className={styles.userEmail}>{user.email}</span>
                  </div>
                  <div className={styles.dropdownDivider}></div>
                  <Link to={lp('/dashboard')} className={styles.dropdownItem}>
                    <CreditCard size={16} />{t('nav.billing')}
                  </Link>
                  <a href={appUrl} className={styles.dropdownItem}>
                    <ExternalLink size={16} />{t('nav.goToApp')}
                  </a>
                  <button onClick={handleSignOut} className={styles.dropdownItem}>
                    <LogOut size={16} />{t('nav.signOut')}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to={lp('/auth')} className={styles.ctaButton}>{t('nav.signIn')}</Link>
          )}
        </div>

        <button
          className={styles.mobileMenuButton}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenu}>
          {navLinks.map(link => (
            <Link
              key={link.path}
              to={lp(link.path)}
              className={`${styles.mobileNavLink} ${location.pathname.includes(link.path) ? styles.active : ''}`}
            >
              {t(link.labelKey)}
            </Link>
          ))}

          {loading ? null : user ? (
            <>
              <div className={styles.mobileUserInfo}>
                <span className={styles.mobileUserAvatar}>{getInitials(user.displayName, user.email)}</span>
                <div>
                  <span className={styles.mobileUserName}>{user.displayName || 'User'}</span>
                  <span className={styles.mobileUserEmail}>{user.email}</span>
                </div>
              </div>
              <Link to={lp('/dashboard')} className={styles.mobileNavLink}>{t('nav.billing')}</Link>
              <a href={appUrl} className={styles.mobileCta}>{t('nav.goToApp')}</a>
              <button onClick={handleSignOut} className={styles.mobileSignOut}>{t('nav.signOut')}</button>
            </>
          ) : (
            <Link to={lp('/auth')} className={styles.mobileCta}>{t('nav.signIn')}</Link>
          )}

          <div className={styles.mobileLangSection}>
            <span className={styles.mobileLangLabel}>Language</span>
            <div className={styles.mobileLangGrid}>
              {SUPPORTED_LOCALES.map(locale => (
                <button
                  key={locale}
                  className={`${styles.mobileLangOption} ${locale === currentLocale ? styles.langActive : ''}`}
                  onClick={() => handleLocaleSwitch(locale)}
                >
                  <img src={`https://flagcdn.com/20x15/${LOCALE_FLAG[locale]}.png`} width="20" height="15" alt="" />
                  <span>{LOCALE_META[locale]?.native}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}
